<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="customerBalanceSuccessCount.length || customerBalanceFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="customerBalanceSuccessCount"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ customerBalanceSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="customerBalanceFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="customerBalanceFailedReport"
            :items="customerBalanceFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row>
        <b-col
          md="6"
          class="mb-2"
        >
          <validation-observer ref="dataMigrationCustomerCreationRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col
              md="5"
              class="mt-2"
            >
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && parseExcelFile.length"
                :label="$t('Create Customer Balance')"
                size="md"
                :loading="isLoading"
                @click="handleCreate"
              />
            </b-col>
            <b-col
              md="7"
              class="mt-2"
            >
              <Button
                class="ml-2"
                align="end"
                :label="$t('Download Customer Balance Migration Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import Button from '../../../components/button/Button.vue'
import { toKobo } from '@/helpers/formatAmount'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../../helpers/data'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    BAlert,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'referenceNumber', sortable: true, label: 'Old Reference Number (Current/Savings Only)' },
        { key: 'branchCode', sortable: true, label: 'Branch Code' },
        { key: 'productCode', sortable: true, label: 'Product Code' },
        { key: 'amount', sortable: true, label: 'Ledger Balance' },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      customerBalanceSuccessCount: [],
      customerBalanceFailed: [],
      sortDesc: false,
      sortBy: '',
    }
  },
  methods: {
    ...mapActions({
      migrateCustomerBalance: 'batch/migrateCustomerBalance',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleCreate() {
      this.$refs.dataMigrationCustomerCreationRules.validate().then(success => {
        if (success) {
          this.confirm(this.migrateCustomerBalanceAction, { text: this.$t('Are you sure you want to Create Customer Balance Migration?') })
        }
      })
    },
    async migrateCustomerBalanceAction() {
      try {
        this.customerBalanceSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          referenceNumber: item.referenceNumber,
          amount: item.amount ? Number(toKobo(item.amount).toFixed()) : null,
        }))
        const response = await this.migrateCustomerBalance(formData)
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          if (data.successCount > 1) {
            const text1 = this.$t('Customers Balance was successfully created')
            this.customerBalanceSuccessCount = `(${data.successCount}) ${text1}`
          } else {
            const text2 = this.$t('Customer Balance was successfully created')
            this.customerBalanceSuccessCount = `(${data.successCount}) ${text2}`
          }
          this.customerBalanceFailed = data.failed
          this.isDisabled = false
          this.isLoading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const errorText = this.$t('Your request to create Customer Balance failed. Reason')
          this.alertError({ text: `${errorText}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'dd/mm/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            referenceNumber: items['Old Reference Number (Current/Savings Only)'],
            branchCode: items['Branch Code'],
            productCode: items['Product Code'],
            amount: items['Ledger Balance'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Customer Balance Migration Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const CASA_CREDIT_BALANCES_MIGRATION_TEMPLATE = 6
        const response = await this.downloadAccountFormat(CASA_CREDIT_BALANCES_MIGRATION_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Customer-Balance-Data-Migration.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download data migration for Customer Balance Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const alertText = this.$t('Your request to download data migration for Customer Balance Format failed. Reason')
          this.alertError({ text: `${alertText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
